import type { ViewEvent } from '@/modules/Editor/eventBus'
import { CameraEvents, RasterManagerEvents, ViewEvents } from '@/modules/Editor/eventBus'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import type { InvalidatedRasterRegion, Raster } from '@/modules/Editor/models/raster/Raster'
import { assertVoxelRaster } from '@/modules/Editor/models/raster/assertVoxelRaster'
import { RasterTypes } from '@/modules/Editor/models/raster/rasterTypes'
import { RasterTooLargeError } from '@/modules/Editor/models/raster/deserializeImageRaster'
import {
  clearCanvas,
  getSegmentColorWithAlpha,
  renderMasks,
} from '@/modules/Editor/plugins/mask/MaskRenderer'
import { getPrimaryViewFromView } from '@/modules/Editor/plugins/mask/utils/shared/getPrimaryViewFromView'
import { Plane } from '@/modules/Editor/utils/raster/Plane'
import { getRasterPlaneFromRadiologySlotName } from '@/modules/Editor/utils/raster/getRasterPlaneFromRadiologySlotName'
import { getRasterBufferAccessorForPlane } from '@/modules/Editor/utils/raster/getRasterBufferAccessorForPlane'
import { isDicomView } from '@/modules/Editor/utils/radiology/isDicomView'
import { shouldApplyRasterScaling } from '@/modules/Editor/plugins/mask/utils/shared/shouldApplyRasterScaling'
import type { View } from '@/modules/Editor/views/view'
import { setContext } from '@/services/sentry'

import { Events as LayerEvents, Layer } from './layer'
import type { RenderableItem } from './object2D'
import type { RGB } from '@/modules/Editor/types'
import type { IPoint } from '@/core/annotations'
import { getTopLeftOfScaledPixel } from '@/modules/Editor/plugins/brush/utils/getTopLeftOfScaledPixel'
import { getVoxelRasterDimensions } from '@/modules/Editor/plugins/brush/utils/getVoxelRasterDimensions'
import { mark } from '@/performance/mark'
import { RASTER_RENDER_END, RASTER_RENDER_START } from '@/performance/keys'
import {
  WebGLMaskRenderer,
  type WebGLCameraProxy,
  type WebGLRasterDimensions,
} from '@/modules/Editor/plugins/mask/WebGLMaskRenderer'
import { ImageRaster } from '@/modules/Editor/models/raster/ImageRaster'

const USE_OPTIMISED_3D_INVALIDATION = false

/**
 * Manages rendering process for rasters independently of other
 * other annotations.
 *
 * Still has support for `Object2D` annotations that may come up
 * as subannotations for Rasters in the future.
 */
export class RasterLayer extends Layer {
  private _rasters: Set<string> = new Set()
  private readonly view: View
  private readonly viewId: string
  private webGLCanvas: HTMLCanvasElement | null = null
  private webGLMaskRenderer: WebGLMaskRenderer | null = null

  private get isOnPrimaryView(): boolean {
    return !isDicomView(this.view) || this.view.isPrimary
  }

  constructor(view: View) {
    super()

    if (this._canvas) {
      this._canvas.classList.add('raster-layer')
    }
    this.view = view
    this.viewId = view.id

    ViewEvents.imageFilterChanged.on(this.invalidateRasterInView)
    ViewEvents.currentFrameIndexChanged.on(this.onCurrentFrameIndexChanged)

    CameraEvents.scaleChanged.on(this.triggerRerender)
    CameraEvents.offsetChanged.on(this.triggerRerender)

    RasterManagerEvents.videoRasterKeyframeDeleted.on(this.invalidateRasterInView)
    RasterManagerEvents.rasterUpdated.on(this.triggerRerender)
    RasterManagerEvents.rasterDeleted.on(this.onRasterDelete)

    // Initialize WebGLMaskRenderer
    try {
      this.webGLCanvas = document.createElement('canvas')
      const gl = this.webGLCanvas.getContext('webgl2', { preserveDrawingBuffer: false })
      if (gl) {
        this.webGLMaskRenderer = new WebGLMaskRenderer(gl)
      } else {
        console.warn(
          '[RasterLayer] WebGL context not available. Falling back to 2D canvas rendering for ImageRasters.',
        )
        this.webGLCanvas = null
      }
    } catch {
      console.error('[RasterLayer] Error initializing WebGLMaskRenderer. See Sentry for details.')
      this.webGLMaskRenderer = null
      this.webGLCanvas = null
    }
  }

  /**
   * Callback which invalidates the raster being rendered on this layer.
   * This is so that (part of) the cached canvas can be recalculated as it is
   * now invalid.
   *
   * In the case of a VoxelRaster, we invalidate only what is needed based on the plane
   * (as potentially only one row is invalidated if this is a reformat).
   */
  private invalidateRasterInView = (e?: ViewEvent): void => {
    if (e && e.viewId !== this.viewId) {
      return
    }

    // Invalidate every raster associated with the file, not just the writable
    // (default-group) one — consensus review can have multiple raster overlays
    // sharing the file, and they each have their own per-frame buffer and
    // `cachedCanvas` that must be refreshed when the frame changes.
    const rasters = this.getRastersForFileInPrimaryView()

    if (rasters.length === 0) {
      return
    }

    rasters.forEach((raster) => {
      // We have an issue with using the optimised 3D invalidation when a file is acquired on non
      // axial plane.
      // Since the performance boost is not too significant, we can just invalidate the whole raster
      // for now, until we have a better solution, and we can remove the flag
      if (!USE_OPTIMISED_3D_INVALIDATION) {
        raster.invalidateAll()
        return
      }

      if (raster.type === RasterTypes.VOXEL) {
        // An optimisation to only invalidate the plane, so
        // that other views need not update if they don't intersect.
        const voxelRaster = assertVoxelRaster(raster)
        const { slot_name, metadata } = this.view.fileManager.file
        const currentFrameIndex = this.view.currentFrameIndex
        const plane = getRasterPlaneFromRadiologySlotName(slot_name, metadata)

        if (plane === undefined) {
          // optimistically just re-render everything
          voxelRaster.invalidateAll()
        } else if (plane === Plane.X) {
          voxelRaster.invalidateXPlane(currentFrameIndex)
        } else if (plane === Plane.Y) {
          voxelRaster.invalidateYPlane(currentFrameIndex)
        } else if (plane === Plane.Z) {
          voxelRaster.invalidateZPlane(currentFrameIndex)
        }
      } else {
        raster.invalidateAll()
      }
    })
  }

  /**
   * Call invalidate if there is a raster image on this layer when the frame index changes.
   */
  private onCurrentFrameIndexChanged = (e?: ViewEvent): void => {
    if (e && e.viewId !== this.viewId) {
      return
    }

    if (this.getRastersForFileInPrimaryView().length === 0) {
      return
    }

    this.invalidateRasterInView()
  }

  public hitMaskRegion(point: IPoint): RenderableItem['id'] | undefined {
    // Walk all rasters for the file and return the first hit. `render()` draws
    // non-default consensus overlays in insertion order on top of the default
    // raster, so the last-inserted overlay is visually on top. Walk overlays
    // in REVERSE insertion order here so the topmost visual layer wins the
    // hit; check the default raster last. Non-default raster mappings have
    // been rewritten by `resolveRasterMapping` to point at default-group
    // annotation ids, so every returned id resolves in the annotation manager.
    const rasters = this.getRastersForFileInPrimaryView()
    if (rasters.length === 0) {
      return
    }

    const overlays: Raster[] = []
    let defaultRaster: Raster | undefined
    for (const raster of rasters) {
      if ((raster.annotationGroupId ?? null) === null) {
        defaultRaster = raster
      } else {
        overlays.push(raster)
      }
    }
    overlays.reverse()
    const orderedRasters = defaultRaster ? [...overlays, defaultRaster] : overlays

    const firstRaster = orderedRasters[0]
    if (
      point.x < 0 ||
      point.y < 0 ||
      point.x >= firstRaster.width ||
      point.y >= firstRaster.height
    ) {
      // point is outside the raster, can't be hitting any region
      return
    }

    // get the scaled image point coordinates (if/when needed)
    const { voxelCoordinate: scaledPoint } = getTopLeftOfScaledPixel(
      { x: Math.floor(point.x), y: Math.floor(point.y) },
      this.view.fileManager.file.metadata,
      getVoxelRasterDimensions(this.view),
    )

    for (const raster of orderedRasters) {
      const rasterBufferAccessor = raster.getActiveBufferForRender()
      if (!rasterBufferAccessor) {
        continue
      }

      const labelIndex = rasterBufferAccessor.get(scaledPoint.x, scaledPoint.y)
      if (labelIndex === undefined || labelIndex === 0) {
        continue
      }

      const maskAnnotationId = raster.getAnnotationMapping(labelIndex)
      if (!maskAnnotationId || this.view.annotationManager.isHidden(maskAnnotationId)) {
        continue
      }

      return maskAnnotationId
    }

    return
  }

  private onRasterDelete = (e: ViewEvent, rasterId: string): void => {
    e.viewId === this.viewId && this.removeRaster(rasterId)
  }

  private triggerRerender = (e: ViewEvent): void => {
    if (e.viewId !== this.viewId) {
      return
    }
    this._hasChanges = true
    this.render()
  }

  public getRasterIds(): string[] {
    return Array.from(this.rasters.values())
  }

  public addRaster(rasterId: string): void {
    this._rasters.add(rasterId)
    this._canvas?.classList.add('has-raster')
    this.changed()
  }

  public removeRaster(rasterId: string): void {
    this._rasters.delete(rasterId)
    if (this._rasters.size === 0) {
      this._canvas?.classList.remove('has-raster')
    }
    this.changed()
  }

  get rasters(): Set<string> {
    if (this.isOnPrimaryView) {
      return this._rasters
    }

    // On initial render, the layout object might not yet be instantiated.
    if (!this.view.editor.layout) {
      return new Set()
    }

    const primaryView = getPrimaryViewFromView(this.view)
    const primaryViewRasterLayer = primaryView?.rasterAnnotationLayer

    if (!primaryViewRasterLayer) {
      // probably not yet initialised, return blank set.
      return new Set()
    }

    const rasterIds = primaryViewRasterLayer.getRasterIds()
    const rasterIdSet = new Set<string>()

    rasterIds.forEach((rasterId) => rasterIdSet.add(rasterId))

    return rasterIdSet
  }

  private _hiddenItems: Set<RenderableItem['id']> = new Set()

  public hideAll(ids?: RenderableItem['id'][]): void {
    if (ids) {
      ids.forEach((id) => this._hiddenItems.add(id))
    } else {
      for (const id of this._rasters.keys()) {
        this._hiddenItems.add(id)
      }
    }

    // Raster display control requires invalidation, not just canvas redrawing,
    // as all raster have shared IDs.
    this.invalidateRasterInView()
    this.changed()
  }

  public showAll(ids?: RenderableItem['id'][]): void {
    if (ids) {
      ids.forEach((id) => this._hiddenItems.delete(id))
    } else {
      this._hiddenItems.clear()
    }

    // Raster display control requires invalidation, not just canvas redrawing,
    // as all raster have shared IDs.
    this.invalidateRasterInView()
    this.changed()
  }

  /**
   * Renders groups of mask annotations to single rasters in one draw call,
   * using the `Annotation` data to style the rendering of different
   * portions of the raster.
   *
   * If the layer has no changes it will skip the re-render.
   */
  public render(): void {
    if (!this.context) {
      return
    }
    if (!this._hasChanges) {
      return
    }

    mark(RASTER_RENDER_START)
    this._hasChanges = false

    this.emit(LayerEvents.BEFORE_RENDER, this.context, this.canvas)

    // Clear once before iterating so multiple rasters composite onto the
    // same canvas (used by consensus review to overlay non-champion rasters
    // on top of the champion). The per-raster render paths skip their own
    // clears. `clearCanvas` resets the transform to identity first so a
    // non-identity transform left by a BEFORE_RENDER listener doesn't shift
    // the clear into the wrong coordinate space.
    if (this.canvas) {
      clearCanvas(this.context)
    }

    // Always render the default-group (writable) raster first so non-default
    // overlays draw on top regardless of insertion order into `_rasters`.
    const orderedRasterIds = Array.from(this.rasters).sort((a, b) => {
      const aGroup = this.view.rasterManager.getRaster(a)?.annotationGroupId ?? null
      const bGroup = this.view.rasterManager.getRaster(b)?.annotationGroupId ?? null
      return (aGroup === null ? 0 : 1) - (bGroup === null ? 0 : 1)
    })

    orderedRasterIds.forEach((rasterId) => this.renderRaster(rasterId))

    // Render the subannotations (TODO: Not using these yet)
    Object.values(this._renderPool).forEach((item) => {
      try {
        item.render(this.context, this.canvas)
      } catch (e) {
        setContext('error', { error: e })
        console.error('V2 Raster layer failed to render object')
      }
    })

    this.emit(LayerEvents.RENDER, this.context, this.canvas)

    mark(RASTER_RENDER_END)
  }

  private renderRasterWithWebGL(raster: ImageRaster): void {
    if (!this.context || !this.canvas) {
      throw new Error('Attempted to render without a valid 2D context or canvas.')
    }

    if (!this.webGLMaskRenderer || !this.webGLCanvas) {
      throw new Error('WebGLMaskRenderer or webGLCanvas not found.')
    }

    // Ensure webGLCanvas (offscreen) dimensions match the main view's camera/viewport size
    if (
      this.webGLCanvas.width !== this.view.camera.width ||
      this.webGLCanvas.height !== this.view.camera.height
    ) {
      this.webGLCanvas.width = this.view.camera.width
      this.webGLCanvas.height = this.view.camera.height
    }

    const { labelColorMap } = this.getCommonRenderables(this.view, raster)

    try {
      // Update mask texture if the raster data itself has changed
      if (raster.isInvalidated()) {
        const invalidatedRegionData = raster.getInvalidatedSubBuffer()
        this.webGLMaskRenderer.updateMask(raster, invalidatedRegionData ?? undefined)
      } else {
        // Ensure texture is initialized if not already, even if not invalidated right now
        // (e.g. first render, or WebGL context was lost)
        this.webGLMaskRenderer.updateMask(raster)
      }

      // Always update the color map as selection/highlighting can change colors
      // without invalidating the raster pixel data.
      this.webGLMaskRenderer.updateColorMap(labelColorMap)
    } catch (error) {
      if (error instanceof RasterTooLargeError) {
        RasterManagerEvents.rasterError.emit({ viewId: this.viewId }, { error })

        this.webGLMaskRenderer.destroy()
        this.webGLMaskRenderer = null
        this.webGLCanvas.remove()
        this.webGLCanvas = null

        return
      }
      throw error
    }

    // Prepare camera and raster dimension proxies for the WebGL renderer
    const cameraScale = this.view.camera.scale
    const cameraProxy: WebGLCameraProxy = {
      offset: this.view.camera.offset,
      scale: { x: cameraScale, y: cameraScale }, // Camera.scale is a number, WebGLCameraProxy expects IPoint
      width: this.view.camera.width, // Viewport width
      height: this.view.camera.height, // Viewport height
    }
    const rasterDimensions: WebGLRasterDimensions = {
      width: raster.width,
      height: raster.height,
    }

    this.webGLMaskRenderer.render(cameraProxy, rasterDimensions)

    // The webGLCanvas now contains the correctly panned/zoomed mask part for the current viewport.
    // Draw it directly at (0,0) onto the main layer's canvas. The layer
    // canvas is cleared once at the start of `render()`, so we don't clear
    // here — successive draws composite via the default `source-over`.
    this.context.drawImage(this.webGLCanvas, 0, 0)

    // If the raster was invalidated and its data has now been used to update the WebGL texture,
    // clear the invalidation state.
    if (raster.isInvalidated()) {
      raster.clearInvalidation()
    }
  }

  public renderRaster = (rasterId: string): void => {
    const raster = this.view.rasterManager.getRaster(rasterId)
    const isImageRaster = raster instanceof ImageRaster

    if (raster && isImageRaster && this.view.editor.featureFlags.WEB_GL_MASK_RENDERING) {
      try {
        this.renderRasterWithWebGL(raster)

        // Stop here, do not fall back to the non-WebGL path
        return
      } catch (e) {
        // Fallback handled by continuing to the non-WebGL path below
        setContext('error', { rasterId, viewId: this.viewId, error: e })
        console.error(
          '[RasterLayer] Error during WebGL rendering, falling back. See Sentry for details.',
        )
      }
    }

    // Fallback or default rendering path for non-ImageRasters or if WebGL rendering failed
    if (shouldApplyRasterScaling(this.view)) {
      this.renderRasterWithScaling(rasterId)
    } else {
      this.renderRasterWithoutScaling(rasterId)
    }
  }

  /**
   * Gets every raster (default and consensus overlays) associated with the
   * file in the primary view.
   */
  private getRastersForFileInPrimaryView(): Raster[] {
    if (this.isOnPrimaryView) {
      return this.view.rasterManager.getRastersForFileInView()
    }

    const primaryView = getPrimaryViewFromView(this.view)

    return primaryView?.rasterManager.getRastersForFileInView() ?? []
  }

  private renderRasterWithScaling(rasterId: string): void {
    const { view } = this
    const { slot_name, metadata } = this.view.fileManager.file
    const currentFrameIndex = this.view.currentFrameIndex
    const plane = getRasterPlaneFromRadiologySlotName(slot_name, metadata)

    if (!plane) {
      throw new Error('No plane found for given slot name')
    }

    const primaryView = getPrimaryViewFromView(this.view)

    if (!primaryView) {
      // Views not set up yet, return early
      return
    }

    const raster = primaryView.rasterManager.getRaster(rasterId)

    if (!raster || raster.type !== RasterTypes.VOXEL) {
      throw new Error('Volumetric raster not found')
    }

    const voxelRaster = assertVoxelRaster(raster)

    const rasterBufferAccessor = getRasterBufferAccessorForPlane(
      voxelRaster,
      plane,
      currentFrameIndex,
      metadata?.primary_plane,
    )

    const { labelColorMap } = this.getCommonRenderables(primaryView, voxelRaster)

    let cachedCanvas: HTMLCanvasElement | undefined
    let invalidatedRasterRegion: InvalidatedRasterRegion

    if (plane === Plane.X) {
      invalidatedRasterRegion = voxelRaster.planeXInvalidationRegion
      cachedCanvas = voxelRaster.planeXCachedCanvas
    } else if (plane === Plane.Y) {
      invalidatedRasterRegion = voxelRaster.planeYInvalidationRegion
      cachedCanvas = voxelRaster.planeYCachedCanvas
    } else if (plane === Plane.Z) {
      invalidatedRasterRegion = voxelRaster.invalidatedRegion
      cachedCanvas = voxelRaster.cachedCanvas
    } else {
      throw new Error(`Unknown medical plane: ${plane}`)
    }

    const fileMetadata = view.fileManager.file.metadata

    if (!fileMetadata) {
      throw new Error('No file metadata present, we require this for VoxelRaster scaling.')
    }

    const viewWidth = fileMetadata.width
    const viewHeight = fileMetadata.height

    const targetViewportSize = {
      x: viewWidth,
      y: viewHeight,
    }

    if (!this.context) {
      return
    }

    try {
      renderMasks(this.context, view.camera, {
        rasterBufferAccessor,
        cachedCanvas,
        invalidatedRasterRegion,
        labelColorMap,
        targetViewportSize,
        forceFullRender: true,
        skipClear: true,
      })
    } catch (error) {
      if (error instanceof Error) {
        RasterManagerEvents.rasterError.emit(
          { viewId: this.viewId },
          { error: new RasterTooLargeError(error.message) },
        )
      }
    }
  }

  /**
   * Renders the raster on an image, video, or primary DICOM.
   */
  private renderRasterWithoutScaling = (rasterId: string): void => {
    const raster = this.view.rasterManager.getRaster(rasterId)

    if (raster === undefined) {
      throw new Error('Raster referened by layer is not found in RasterManager')
    }

    const rasterBufferAccessor = raster.getActiveBufferForRender()
    const { labelColorMap } = this.getCommonRenderables(this.view, raster)

    const { cachedCanvas, invalidatedRegion } = raster
    const isInvalidated = raster.isInvalidated()

    if (!this.context) {
      return
    }

    try {
      renderMasks(this.context, this.view.camera, {
        rasterBufferAccessor,
        cachedCanvas,
        isInvalidated,
        invalidatedRasterRegion: invalidatedRegion,
        labelColorMap,
        forceFullRender: true,
        skipClear: true,
      })
    } catch (error) {
      if (error instanceof Error) {
        RasterManagerEvents.rasterError.emit(
          { viewId: this.viewId },
          { error: new RasterTooLargeError(error.message) },
        )
      }
    }

    raster.clearInvalidation()
  }

  private getMaskImageColor(
    annotation: Annotation,
    raster: Raster,
    defaultOpacity: number,
  ): number {
    const MaskOpacity = {
      Selected: Math.min(100, defaultOpacity + 25),
      Highlighted: Math.min(75, defaultOpacity + 10),
      Default: defaultOpacity,
    }

    // selected
    if (raster.selectedAnnotationIds.includes(annotation.id)) {
      return getSegmentColorWithAlpha(annotation.color, MaskOpacity.Selected)
    }

    // highlighted
    if (raster.highlightedAnnotationIds.includes(annotation.id)) {
      return getSegmentColorWithAlpha(annotation.color, MaskOpacity.Highlighted)
    }

    return getSegmentColorWithAlpha(annotation.color, MaskOpacity.Default)
  }

  public getCommonRenderables(
    view: View,
    raster: Raster,
  ): {
    labelColorMap: Record<string, number>
    labelRGBColorMap: Record<string, RGB>
  } {
    const { imageFilter } = view
    const { labelsOnRaster, annotationIdsOnRaster } = raster
    const visibleAnnotations: Annotation[] = []

    // Get annotations required for colors, etc
    annotationIdsOnRaster.forEach((annotationId) => {
      const annotation = this.getMaskAnnotationForRaster(view, raster, annotationId)

      if (annotation && !this._hiddenItems.has(annotation.id)) {
        visibleAnnotations.push(annotation)
      }
    })

    const labelsToAnnotationIds: Record<number, string> = {}

    labelsOnRaster.forEach((labelIndex: number) => {
      const annotationIdForLabel = raster.getAnnotationMapping(labelIndex)

      if (annotationIdForLabel) {
        labelsToAnnotationIds[Number(labelIndex)] = annotationIdForLabel
      }
    })

    const labelColorMap: Record<string, number> = {}
    const labelRGBColorMap: Record<string, RGB> = {}

    Object.keys(labelsToAnnotationIds).forEach((labelIndex) => {
      const annotationId = labelsToAnnotationIds[Number(labelIndex)]
      const annotation = visibleAnnotations.find((annotation) => annotation.id === annotationId)
      if (!annotation) {
        return
      }

      labelColorMap[labelIndex] = this.getMaskImageColor(annotation, raster, imageFilter.opacity)
      labelRGBColorMap[labelIndex] = annotation.color
    })

    return { labelColorMap, labelRGBColorMap }
  }

  private getMaskAnnotationForRaster = (
    view: View,
    raster: Raster,
    annotationId: string,
  ): Annotation | undefined => {
    // Check the annotation manager
    const annotation = view.annotationManager.getAnnotation(annotationId)

    if (annotation) {
      return annotation
    }

    // Fallback to checking if we have a temporary in progress annotation
    // (e.g. during a brush stroke for an as-of-yet uncreated mask annotation)
    return raster.getInProgressAnnotation(annotationId)
  }

  clear(): void {
    super.clear()
    this._rasters.clear()
  }

  destroy(): void {
    super.destroy()

    ViewEvents.imageFilterChanged.off(this.invalidateRasterInView)
    ViewEvents.currentFrameIndexChanged.off(this.onCurrentFrameIndexChanged)

    CameraEvents.scaleChanged.off(this.triggerRerender)
    CameraEvents.offsetChanged.off(this.triggerRerender)

    RasterManagerEvents.videoRasterKeyframeDeleted.off(this.invalidateRasterInView)
    RasterManagerEvents.rasterUpdated.off(this.triggerRerender)
    RasterManagerEvents.rasterDeleted.off(this.onRasterDelete)

    this.webGLMaskRenderer?.destroy()
    this.webGLMaskRenderer = null
    this.webGLCanvas = null
  }
}
